import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { getEnvironment } from "../../../../analytics/posthog";

const DEFAULT_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="9" x="3" y="3" rx="1"></rect><rect width="7" height="5" x="14" y="3" rx="1"></rect><rect width="7" height="9" x="14" y="12" rx="1"></rect><rect width="7" height="5" x="3" y="16" rx="1"></rect></svg>';

/** Strip script/event attributes to mitigate XSS when injecting config-driven icon HTML. Only use config from a trusted source (e.g. app-controlled public/ or vetted REACT_APP_ANALYTICS_SIDEBAR_CONFIG_URL). */
function sanitizeIconHtml(html) {
  if (typeof html !== "string") return "";
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/\s on\w+=["'][^"']*["']/gi, "")
    .replace(/\s on\w+=\s*[^\s>]+/gi, "");
}

const TITLE_PREFIX = process.env.REACT_APP_ANALYTICS_TITLE_PREFIX ?? "~";
const DEMO_API_TITLE_PREFIX = "|Demo ";
const normalize = (s) => s?.toLowerCase().trim() ?? "";

function isDemoEnv() {
  if (getEnvironment() === "demo") return true;
  return (process.env.REACT_APP_API_URL ?? "").includes("api-demo");
}

function withDemoApiTitle(title) {
  if (!isDemoEnv() || typeof title !== "string" || !title) return title;
  return title.startsWith(DEMO_API_TITLE_PREFIX) ? title : `${DEMO_API_TITLE_PREFIX}${title}`;
}

function stripTitlePrefix(title) {
  if (typeof title !== "string") return title ?? "";
  return TITLE_PREFIX && title.startsWith(TITLE_PREFIX)
    ? title.slice(TITLE_PREFIX.length).trim()
    : title.trim();
}

function mergeIdsIntoConfig(config, dashboards) {
  if (!dashboards?.length) return config;

  // Strip the configurable prefix (e.g. "~") before matching
  const ndash = dashboards.map((d) => ({
    id: d.id,
    norm: normalize(stripTitlePrefix(d.title)),
  }));
  const byTitle = Object.fromEntries(ndash.map((d) => [d.norm, d.id]));

  const sections = (config.sections || []).map((sec) => ({
    ...sec,
    items: (sec.items || []).map((item) => {
      const apiTitle = withDemoApiTitle(item.apiTitle ?? item.label);
      return {
        ...item,
        apiTitle,
        dashboardId: byTitle[normalize(stripTitlePrefix(apiTitle))] ?? item.dashboardId,
      };
    }),
  }));

  // Any dashboard not matched to a config item gets appended so nothing is hidden
  const matchedIds = new Set(
    sections.flatMap((s) => s.items.map((i) => i.dashboardId)).filter(Boolean)
  );
  const unmatched = dashboards.filter((d) => !matchedIds.has(d.id));

  if (process.env.NODE_ENV !== "production") {
    const unmatchedTitles = unmatched.map((d) => `"${d.title}" (stripped: "${stripTitlePrefix(d.title)}")`);
    if (unmatchedTitles.length) {
      console.warn("[Sidebar] Unmatched dashboards (update apiTitle in analytics-sidebar.config.json):", unmatchedTitles);
    }
  }

  if (unmatched.length > 0) {
    sections.push({
      section: "OTHER DASHBOARDS",
      icon: DEFAULT_ICON,
      items: unmatched.map((d) => ({
        label: stripTitlePrefix(d.title),
        dashboardId: d.id,
        icon: DEFAULT_ICON,
      })),
    });
  }

  return { ...config, sections };
}

export default function RecruitmentSidebar({ activeDashboardId, onClick, dashboards, onActiveLabel }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const configUrl = useMemo(
    () =>
      process.env.REACT_APP_ANALYTICS_SIDEBAR_CONFIG_URL ||
      `${process.env.PUBLIC_URL || ""}/analytics-sidebar.config.json`.replace(/([^:])\/+/g, "$1/"),
    []
  );

  useEffect(() => {
    if (!dashboards?.length) {
      setConfig(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetch(configUrl)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("Invalid config"))))
      .then((data) => {
        if (!cancelled && data && Array.isArray(data.sections)) {
          setConfig(mergeIdsIntoConfig(data, dashboards));
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV !== "production") {
          console.error("Failed to load analytics sidebar config:", err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [configUrl, dashboards]);

  const { title = "Analytics", subtitle = "", sections } = config ?? {};

  useEffect(() => {
    if (!onActiveLabel || !sections || activeDashboardId == null) return;
    const allItems = sections.flatMap((s) => s.items ?? []);
    const active = allItems.find((item) => item.dashboardId === activeDashboardId);
    if (active?.label) onActiveLabel(active.label);
  }, [activeDashboardId, sections, onActiveLabel]);

  if (loading) {
    return (
      <StyledSidebar>
        <StyledHeader>
          <StyledTitle>...</StyledTitle>
          <StyledSubtitle>...</StyledSubtitle>
        </StyledHeader>
      </StyledSidebar>
    );
  }

  return (
    <StyledSidebar>
      <StyledHeader>
        <StyledTitle>{title}</StyledTitle>
        <StyledSubtitle>{subtitle}</StyledSubtitle>
      </StyledHeader>

      <StyledMenuScroll>
        {sections?.filter((section) => section.items?.some((item) => item.dashboardId != null))?.map((section) => (
          <React.Fragment key={section.section}>
            <StyledSection>
              <IconSpan dangerouslySetInnerHTML={{ __html: sanitizeIconHtml(section.icon) || DEFAULT_ICON }} />
              <b>{section.section}</b>
            </StyledSection>

            {section?.items
              ?.filter((item) => item.dashboardId != null)
              ?.map((item) => (
              <StyledLink
                key={item.dashboardId}
                className={activeDashboardId === item.dashboardId ? "active" : ""}
                onClick={() => onClick(item.dashboardId)}
              >
                <IconSpan dangerouslySetInnerHTML={{ __html: sanitizeIconHtml(item.icon) || DEFAULT_ICON }} />
                {item.label}
              </StyledLink>
            ))}

            <StyledDivider />
          </React.Fragment>
        ))}
      </StyledMenuScroll>
    </StyledSidebar>
  );
}

const StyledSidebar = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
`;

const StyledMenuScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const StyledHeader = styled.div`
  background: ${({ theme }) =>
    theme?.dark
      ? "linear-gradient(135deg, rgb(20, 45, 85), rgb(13, 35, 73))"
      : "linear-gradient(135deg, rgb(13, 35, 73), rgb(20, 55, 110))"};
  padding: 16px;
  border-radius: 14px;
  color: #ffffff;
  margin-bottom: 18px;
  box-shadow: ${({ theme }) =>
    theme?.dark ? "0 8px 24px rgba(0, 0, 0, 0.4)" : "0 12px 24px rgba(13, 35, 73, 0.35)"};
`;

const StyledTitle = styled.div`
  font-family: "Raleway", sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.3px;
`;

const StyledSubtitle = styled.div`
  font-family: "Raleway", sans-serif;
  font-size: 12px;
  opacity: 0.85;
  margin-top: 4px;
`;

const StyledDivider = styled.div`
  border-top: 1px solid
    ${({ theme }) => (theme?.dark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)")};
  margin: 14px 0;
`;

const IconSpan = styled.span`
  flex-shrink: 0;
  margin-right: 4px;
  display: inline-flex;
  align-items: center;

  svg {
    flex-shrink: 0;
  }
`;

const StyledSection = styled.div`
  font-family: "Raleway", sans-serif;
  margin: 18px 0 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) =>
    theme?.dark ? theme?.primaryText || "rgba(255,255,255,0.7)" : theme?.primaryLight2 || "rgb(110, 110, 110)"};
`;

const StyledLink = styled.div`
  font-family: "Raleway", sans-serif;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  margin-left: 6px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => (theme?.dark ? theme?.textColor || "#eaeaea" : theme?.textColor || "rgb(90, 90, 90)")};
  cursor: pointer;
  position: relative;
  transition:
    background 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    background: ${({ theme }) =>
      theme?.dark ? "rgba(255, 255, 255, 0.06)" : "rgba(13, 35, 73, 0.04)"};
    color: ${({ theme }) => (theme?.dark ? "#fff" : theme?.primary || "#0d2349")};
  }

  &.active {
    background: ${({ theme }) =>
      theme?.dark ? "rgba(255, 255, 255, 0.08)" : "rgba(13, 35, 73, 0.06)"};
    color: ${({ theme }) => (theme?.dark ? "#fff" : theme?.primary || "#0d2349")};
    font-weight: 600;
  }

  &.active::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 52%;
    width: 3px;
    background: ${({ theme }) => (theme?.dark ? "rgba(255, 255, 255, 0.9)" : theme?.primary || "#0d2349")};
    border-radius: 0 3px 3px 0;
  }
`;
